import axiosService from '../utils/axios';
import { ActivePromo, PartnerPromoPricing, Promo, PromoAnalytics, PromoFormFields, PromoPlatformSettings } from '../types/promo';

function buildPromoFormData(fields: Partial<PromoFormFields>, mediaFile?: File | null): FormData {
    const formData = new FormData();

    if (mediaFile) {
        formData.append('media', mediaFile);
    }
    if (fields.title !== undefined) formData.append('title', fields.title);
    if (fields.link !== undefined) formData.append('link', fields.link);
    if (fields.priority !== undefined) formData.append('priority', String(fields.priority));
    if (fields.status !== undefined) formData.append('status', fields.status);
    if (fields.utmParams !== undefined) formData.append('utmParams', JSON.stringify(fields.utmParams));
    if (fields.textOverlay !== undefined) formData.append('textOverlay', JSON.stringify(fields.textOverlay));
    if (fields.imagePosition !== undefined) formData.append('imagePosition', JSON.stringify(fields.imagePosition));
    if (fields.placements !== undefined) formData.append('placements', JSON.stringify(fields.placements));
    if (fields.allowedPages !== undefined) formData.append('allowedPages', JSON.stringify(fields.allowedPages));
    if (fields.billingMode !== undefined) formData.append('billingMode', fields.billingMode);
    if (fields.cpmCents !== undefined) formData.append('cpmCents', fields.cpmCents === null ? '' : String(fields.cpmCents));
    if (fields.useCustomCpm !== undefined) formData.append('useCustomCpm', String(fields.useCustomCpm));
    // Partners may set dailyBudgetCents; billingMode/cpmCents remain admin-only
    // and are ignored by partner create/update endpoints.
    if (fields.dailyBudgetCents !== undefined) formData.append('dailyBudgetCents', fields.dailyBudgetCents === null ? '' : String(fields.dailyBudgetCents));

    return formData;
}

// ---------- Admin ----------

export async function getAdminPromos(params?: { status?: string; reviewStatus?: string }): Promise<Promo[]> {
    const { data } = await axiosService.get<{ promos: Promo[] }>('/admin/promos', { params });
    return data.promos;
}

export async function getPromo(id: string): Promise<Promo> {
    const { data } = await axiosService.get<Promo>(`/admin/promos/${id}`);
    return data;
}

export async function createPromo(fields: Partial<PromoFormFields>, mediaFile: File): Promise<Promo> {
    const formData = buildPromoFormData(fields, mediaFile);
    const { data } = await axiosService.post<Promo>('/admin/promos', formData);
    return data;
}

export async function updatePromo(id: string, fields: Partial<PromoFormFields>, mediaFile?: File | null): Promise<Promo> {
    const formData = buildPromoFormData(fields, mediaFile);
    const { data } = await axiosService.put<Promo>(`/admin/promos/${id}`, formData);
    return data;
}

export async function updatePromoStatus(id: string, status: string): Promise<Promo> {
    const { data } = await axiosService.put<Promo>(`/admin/promos/${id}/status`, { status });
    return data;
}

export async function reviewPromo(id: string, reviewStatus: string, reviewNotes?: string, status?: string): Promise<Promo> {
    const { data } = await axiosService.put<Promo>(`/admin/promos/${id}/review`, { reviewStatus, reviewNotes, status });
    return data;
}

export async function assignPromoOwner(
    id: string,
    partnerId: string | null
): Promise<{ promo: Promo; grantedPartnerAccess: boolean; message: string }> {
    const { data } = await axiosService.put<{
        promo: Promo;
        grantedPartnerAccess: boolean;
        message: string;
    }>(`/admin/promos/${id}/owner`, { partnerId });
    return data;
}

export async function deletePromo(id: string): Promise<{ message: string; s3Deleted: boolean }> {
    const { data } = await axiosService.delete(`/admin/promos/${id}`);
    return data;
}

export async function getPromoAnalytics(id: string): Promise<PromoAnalytics> {
    const { data } = await axiosService.get<PromoAnalytics>(`/admin/promos/${id}/analytics`);
    return data;
}

export async function getPromoPlatformSettings(): Promise<PromoPlatformSettings> {
    const { data } = await axiosService.get<PromoPlatformSettings>('/admin/promos/settings');
    return data;
}

export async function updatePromoPlatformSettings(
    fields: Partial<PromoPlatformSettings>
): Promise<PromoPlatformSettings> {
    const { data } = await axiosService.put<PromoPlatformSettings>('/admin/promos/settings', fields);
    return data;
}

// ---------- Partner ----------

export async function getPartnerPromos(): Promise<Promo[]> {
    const { data } = await axiosService.get<{ promos: Promo[] }>('/partner/promos');
    return data.promos;
}

export async function getPartnerPromoPricing(): Promise<PartnerPromoPricing> {
    const { data } = await axiosService.get<PartnerPromoPricing>('/partner/promos/pricing');
    return data;
}

export async function submitPartnerPromo(fields: Partial<PromoFormFields>, mediaFile: File): Promise<Promo> {
    const formData = buildPromoFormData(fields, mediaFile);
    const { data } = await axiosService.post<Promo>('/partner/promos', formData);
    return data;
}

export async function updatePartnerPromo(id: string, fields: Partial<PromoFormFields>, mediaFile?: File | null): Promise<Promo> {
    const formData = buildPromoFormData(fields, mediaFile);
    const { data } = await axiosService.put<Promo>(`/partner/promos/${id}`, formData);
    return data;
}

export async function updatePartnerPromoStatus(id: string, status: string): Promise<Promo> {
    const { data } = await axiosService.put<Promo>(`/partner/promos/${id}/status`, { status });
    return data;
}

export async function deletePartnerPromo(id: string): Promise<{ message: string; s3Deleted: boolean }> {
    const { data } = await axiosService.delete(`/partner/promos/${id}`);
    return data;
}

export async function getPartnerPromoAnalytics(id: string): Promise<PromoAnalytics> {
    const { data } = await axiosService.get<PromoAnalytics>(`/partner/promos/${id}/analytics`);
    return data;
}

// ---------- Public (serving) ----------

export async function getActivePromos(): Promise<ActivePromo[]> {
    const { data } = await axiosService.get<{ promos: ActivePromo[] }>('/promos/active');
    return data.promos;
}

export interface PromoSelectionResult {
    promo: ActivePromo | null;
    serveToken: string | null;
}

// Server-side selection: the backend picks the winning promo for this
// placement (accounting for partner funding/budget) and issues a single-use
// serve token that must be presented to recordImpression / recordClick.
export async function selectPromo(page: string, placement: string, device: string, excludeId?: string): Promise<PromoSelectionResult> {
    const { data } = await axiosService.post<PromoSelectionResult>('/promos/select', { page, placement, device, excludeId });
    return data;
}

// promoId comes from the promo returned by selectPromo; the server also
// independently derives the promo from the serve token and rejects the
// request (400) if they don't match, so a caller can't redeem a token
// against the wrong promo id.
export async function recordImpression(promoId: string, serveToken: string): Promise<void> {
    await axiosService.post(`/promos/${promoId}/impression`, { serveToken });
}

export async function recordClick(promoId: string, serveToken: string): Promise<void> {
    await axiosService.post(`/promos/${promoId}/click`, { serveToken });
}
